import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { hasClerkTokenProvider, getClerkTokenForApi } from './clerkTokenProvider';

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string = '000000',
    public reason: string | string[] | null = null,
  ) {
    const message = Array.isArray(reason) ? reason.join(', ') : reason || 'An error occurred interacting with the API.';
    super(message);
    this.name = 'ApiClientError';

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiClientError);
    }
  }
}

export interface ApiClientConfig extends AxiosRequestConfig {
  baseURL: string;
  onUnauthorized?: () => void;
}

export class ApiClient {
  private client: AxiosInstance;
  private onUnauthorized?: () => void;

  constructor(config: ApiClientConfig) {
    const { onUnauthorized, ...axiosConfig } = config;

    this.onUnauthorized = onUnauthorized;
    this.client = axios.create({
      timeout: 30000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      ...axiosConfig,
    });

    this.client.interceptors.request.use(async config => {
      if (hasClerkTokenProvider()) {
        const freshToken = await getClerkTokenForApi();
        if (freshToken) {
          config.headers['Authorization'] = `Bearer ${freshToken}`;
        }
      }

      const roleOverride = localStorage.getItem('waterlily-role-override');
      if (roleOverride) {
        config.headers['X-Waterlily-Role'] = roleOverride;
      }

      const impersonateAdvisorId = localStorage.getItem('waterlily-impersonate-advisor-id');
      if (impersonateAdvisorId) {
        config.headers['x-impersonate-advisor-id'] = impersonateAdvisorId;
      }

      return config;
    });

  }

  setToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearToken(): void {
    delete this.client.defaults.headers.common['Authorization'];
  }

  async get<T = any>(path: string, params?: Record<string, any>): Promise<T> {
    try {
      const response = await this.client.get<T>(path, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async post<T = any>(path: string, data?: Record<string, any>): Promise<T> {
    try {
      const response = await this.client.post<T>(path, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async put<T = any>(path: string, data?: Record<string, any>): Promise<T> {
    try {
      const response = await this.client.put<T>(path, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async delete<T = any>(path: string, params?: Record<string, any>): Promise<T> {
    try {
      const response = await this.client.delete<T>(path, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): ApiClientError {
    if (axios.isAxiosError(error) && error.response) {
      const { data, status } = error.response;

      if (status === 401 && this.onUnauthorized) {
        this.onUnauthorized();
      }

      if (data) {
        const code = data.code || '000000';
        const reason =
          (typeof data.errorMessage === 'string' ? data.errorMessage : null) ||
          (typeof data.error === 'string' ? data.error : null) ||
          data.errors ||
          data.message ||
          code;
        return new ApiClientError(status, code, reason);
      }
      return new ApiClientError(status);
    }
    throw error;
  }
}
